<template>
  <main class="container-1 terrain-campaigns">
    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <TabNav :links="routesNav" />
    </div>

    <section class="container-body terrain-campaigns__body" aria-live="polite">
      <div class="terrain-campaigns__toolbar">
        <div class="terrain-campaigns__filters">
          <label class="terrain-campaigns__search">
            Buscar por nombre
            <input v-model="search" type="search" placeholder="Buscar campaña…">
          </label>
          <label class="terrain-campaigns__filter-field">
            Estado
            <TerrainSelect v-model="statusFilter" :options="statusOptions" aria-label="Filtrar campañas por estado" />
          </label>
          <TerrainDateRangePicker :start="dateFrom" :end="dateTo" @update:start="dateFrom = $event" @update:end="dateTo = $event" />
          <label class="terrain-campaigns__filter-field">
            Ordenar por
            <TerrainSelect v-model="sortBy" :options="sortOptions" aria-label="Ordenar campañas" />
          </label>
          <button v-if="hasActiveFilters" type="button" class="filters-clear-btn" @click="clearFilters">
            Limpiar filtros
          </button>
        </div>
        <router-link class="terrain-campaigns__new" :to="{ name: 'terrain-campaign-new' }">
          Nueva campaña
        </router-link>
      </div>

      <p v-if="loading" class="terrain-campaigns__status">Cargando campañas…</p>
      <p v-else-if="errorMessage" class="terrain-campaigns__error" role="alert">{{ errorMessage }}</p>
      <template v-else>
        <p v-if="!filteredCampaigns.length" class="terrain-campaigns__empty">
          {{ hasActiveFilters ? 'Ninguna campaña coincide con los filtros.' : 'No hay campañas disponibles.' }}
        </p>
        <ul v-else class="terrain-campaigns__list" aria-label="Campañas de terreno">
          <li v-for="campaign in pagedCampaigns" :key="campaign.id" class="terrain-campaigns__card">
            <div>
              <h3>{{ campaign.name }}</h3>
              <p>{{ dateLabel(campaign.startAt) }} a {{ dateLabel(closeDisplayInstant(campaign.closeAt)) }}</p>
              <div class="terrain-campaigns__tags">
                <router-link
                  v-if="responseCounts[campaign.id] !== undefined"
                  class="terrain-campaigns__tag terrain-campaigns__tag--responses"
                  :to="{ name: 'terrain-campaign-responses', params: { campaignId: campaign.id } }"
                >
                  {{ responseCounts[campaign.id] === null ? '—' : responseCounts[campaign.id] }} respuestas
                </router-link>
                <span class="terrain-campaigns__tag" :class="`terrain-campaigns__tag--${campaign.status}`">
                  {{ statusLabel(campaign.status) }}
                </span>
                <span v-if="campaign.deletedAt" class="terrain-campaigns__tag terrain-campaigns__tag--deleted">
                  Eliminada
                </span>
              </div>
            </div>
            <div class="terrain-campaigns__card-actions">
              <router-link
                class="terrain-campaigns__link terrain-campaigns__link--secondary"
                :to="{ name: 'terrain-campaign-edit', params: { campaignId: campaign.id } }"
              >
                Editar
              </router-link>
              <button
                type="button"
                class="terrain-campaigns__link terrain-campaigns__link--secondary"
                :disabled="!isActive(campaign)"
                :title="isActive(campaign) ? '' : 'Solo disponible para campañas publicadas'"
                @click="openCampaignLink(campaign)"
              >
                Abrir enlace
              </button>
              <button
                v-if="hasShareApi"
                type="button"
                class="terrain-campaigns__icon-btn"
                :disabled="!isActive(campaign)"
                :title="isActive(campaign) ? 'Compartir enlace' : 'Solo disponible para campañas publicadas'"
                aria-label="Compartir enlace"
                @click="shareCampaignLink(campaign)"
              >
                <font-awesome-icon icon="share" />
              </button>
              <button
                type="button"
                class="terrain-campaigns__icon-btn"
                :disabled="!isActive(campaign)"
                :title="isActive(campaign) ? 'Copiar enlace' : 'Solo disponible para campañas publicadas'"
                aria-label="Copiar enlace"
                @click="copyCampaignLink(campaign)"
              >
                <font-awesome-icon icon="copy" />
              </button>
              <button
                type="button"
                class="terrain-campaigns__icon-btn"
                title="Ver en el mapa"
                aria-label="Ver en el mapa"
                @click="openMapModal(campaign)"
              >
                <font-awesome-icon icon="map" />
              </button>
              <button
                v-if="!campaign.deletedAt"
                type="button"
                class="terrain-campaigns__icon-btn"
                title="Eliminar campaña"
                aria-label="Eliminar campaña"
                @click="openDeleteModal(campaign)"
              >
                <font-awesome-icon icon="trash-alt" />
              </button>
              <button
                v-else-if="responseCounts[campaign.id] === 0"
                type="button"
                class="terrain-campaigns__icon-btn terrain-campaigns__icon-btn--danger"
                title="Eliminar para siempre"
                aria-label="Eliminar para siempre"
                @click="openDeleteModal(campaign)"
              >
                <font-awesome-icon icon="times-circle" />
              </button>
            </div>
          </li>
        </ul>

        <modal name="terrain-campaign-map-picker" :width="420" height="auto" :adaptive="true">
          <div class="terrain-campaigns__map-modal">
            <h2>Ver en el mapa</h2>
            <p v-if="mapModalCampaign">Elija el mapa donde ver los puntos de "{{ mapModalCampaign.name }}".</p>

            <p v-if="mapModalLoading" class="terrain-campaigns__map-modal-status">Cargando mapas…</p>
            <p v-else-if="!mapModalOptions.length" class="terrain-campaigns__map-modal-status">
              Esta campaña no tiene mapas de destino configurados.
            </p>
            <ul v-else class="terrain-campaigns__map-modal-list">
              <li v-for="mapOption in mapModalOptions" :key="mapOption.id">
                <button type="button" class="terrain-campaigns__map-modal-option" @click="goToMap(mapOption)">
                  {{ mapOption.name }}
                </button>
              </li>
            </ul>

            <div class="terrain-campaigns__map-modal-actions">
              <button type="button" class="terrain-campaigns__map-modal-cancel" @click="closeMapModal">Cancelar</button>
            </div>
          </div>
        </modal>

        <Pagination
          v-if="totalPages > 1"
          :total-pages="totalPages"
          :current-page="page"
          :page-size="pageSize"
          @pagechanged="onPageChanged"
        />

        <modal name="terrain-campaign-delete" :width="420" height="auto" :adaptive="true">
          <div class="terrain-campaigns__map-modal" v-if="deleteModalCampaign">
            <h2>{{ deleteModalMode === 'hard' ? 'Eliminar en definitiva' : 'Eliminar campaña' }}</h2>
            <p v-if="deleteModalMode === 'hard'">
              Esta acción no se puede deshacer: se borrará por completo "{{ deleteModalCampaign.name }}" y sus datos
              de publicación (destinatarios, borradores). Solo es posible porque todavía no tiene respuestas enviadas.
            </p>
            <p v-else>
              "{{ deleteModalCampaign.name }}" dejará de aparecer en el listado y, si estaba publicada, su capa
              desaparecerá de inmediato de todos los mapas. Sus respuestas y su historial se conservan.
            </p>

            <!-- Opción secundaria: desde el flujo normal de "Eliminar" (soft),
                 ofrecer eliminar en definitiva en su lugar - solo si la
                 campaña no tiene ninguna respuesta enviada. Se valida con un
                 conteo fresco al abrir el modal (deleteModalResponseCount),
                 no con el tag "N respuestas" de la tarjeta: ese se cachea una
                 sola vez por campaña (loadResponseCounts) y puede quedar
                 desactualizado si llegan respuestas nuevas durante la
                 sesión - mostrar esta opción con datos viejos dejaba
                 ofrecer "eliminar en definitiva" en campañas que sí tenían
                 respuestas, aunque el backend igual lo hubiera rechazado. No
                 reemplaza al botón principal: cambia el modo y el usuario
                 debe confirmar de nuevo, para no destruir datos con un solo
                 click. -->
            <button
              v-if="deleteModalMode === 'soft' && deleteModalResponseCount === 0"
              type="button"
              class="terrain-campaigns__map-modal-option-link"
              :disabled="deleteModalSubmitting"
              @click="deleteModalMode = 'hard'"
            >
              Prefiero eliminarla en definitiva
            </button>
            <button
              v-if="deleteModalMode === 'hard' && !deleteModalCampaign.deletedAt"
              type="button"
              class="terrain-campaigns__map-modal-option-link"
              :disabled="deleteModalSubmitting"
              @click="deleteModalMode = 'soft'"
            >
              Volver a solo eliminar
            </button>

            <div class="terrain-campaigns__map-modal-actions">
              <button type="button" class="terrain-campaigns__map-modal-cancel" :disabled="deleteModalSubmitting" @click="closeDeleteModal">
                Cancelar
              </button>
              <button
                type="button"
                :class="deleteModalMode === 'hard' ? 'terrain-campaigns__link terrain-campaigns__link--danger' : 'terrain-campaigns__link'"
                :disabled="deleteModalSubmitting"
                @click="confirmDelete"
              >
                {{ deleteModalSubmitting ? 'Eliminando…' : (deleteModalMode === 'hard' ? 'Eliminar para siempre' : 'Eliminar') }}
              </button>
            </div>
          </div>
        </modal>
      </template>
    </section>
  </main>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import TabNav from "@/components/TabNav.vue";
import Pagination from "@/components/Pagination.vue";
import TerrainSelect from "@/components/terrain/TerrainSelect.vue";
import TerrainDateRangePicker from "@/components/terrain/TerrainDateRangePicker.vue";
import { createTerrainApi } from "@/services/terrain/api.js";
import { terrainNavLinks } from "@/services/terrain/nav.js";

export default {
  name: "TerrainCampaigns",
  components: { Breadcrumb, TabNav, Pagination, TerrainSelect, TerrainDateRangePicker },
  data() {
    return {
      // "" (Todas) muestra solo estados visibles (draft/published/closed);
      // "deleted" es un pseudo-estado aparte ("ver papelera") que solo
      // aparece cuando se selecciona explícitamente - nunca por defecto.
      statusOptions: [
        { value: "", label: "Todas" }, { value: "draft", label: "Borrador" },
        { value: "published", label: "Publicada" }, { value: "closed", label: "Cerrada" },
        { value: "deleted", label: "Eliminadas (papelera)" },
      ],
      sortBy: "",
      sortOptions: [
        { value: "", label: "Orden original" },
        { value: "name-asc", label: "Nombre (A-Z)" },
        { value: "name-desc", label: "Nombre (Z-A)" },
        { value: "start-desc", label: "Inicio más reciente" },
        { value: "start-asc", label: "Inicio más antiguo" },
      ],
      allCampaigns: [],
      loading: true,
      errorMessage: "",
      search: "",
      statusFilter: "",
      dateFrom: "",
      dateTo: "",
      page: 1,
      pageSize: 10,
      responseCounts: {},
      mapModalCampaign: null,
      mapModalOptions: [],
      mapModalLoading: false,
      deleteModalCampaign: null,
      deleteModalMode: null,
      deleteModalSubmitting: false,
      deleteModalResponseCount: null,
    };
  },
  computed: {
    routesBread() { return [{ name: "Terreno" }, { name: "Campañas" }]; },
    routesNav() { return terrainNavLinks((this.$store.getters.getPermissions || {}).terrain); },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
    hasShareApi() { return typeof navigator !== "undefined" && !!navigator.share; },
    hasActiveFilters() { return Boolean(this.search || this.statusFilter || this.dateFrom || this.dateTo || this.sortBy); },
    // Búsqueda y filtros son client-side sobre el lote ya cargado: el
    // backend (GET /campaigns) todavía no soporta filtro por texto/estado/
    // fecha (tarea 15.1, diferida). Para el volumen esperado de campañas
    // esto alcanza; si crece mucho habrá que mover el filtro al servidor.
    filteredCampaigns() {
      const query = this.search.trim().toLowerCase();
      return this.allCampaigns.filter((campaign) => {
        const isDeleted = Boolean(campaign.deletedAt);
        // "deleted" es su propio filtro exclusivo (papelera); cualquier otro
        // valor (incluida "Todas") oculta las eliminadas por defecto.
        if (this.statusFilter === "deleted") {
          if (!isDeleted) return false;
        } else {
          if (isDeleted) return false;
          if (this.statusFilter && campaign.status !== this.statusFilter) return false;
        }
        if (query && !(campaign.name || "").toLowerCase().includes(query)) return false;
        const startDay = (campaign.startAt || "").slice(0, 10);
        if (this.dateFrom && startDay < this.dateFrom) return false;
        if (this.dateTo && startDay > this.dateTo) return false;
        return true;
      });
    },
    totalPages() { return Math.max(1, Math.ceil(this.filteredCampaigns.length / this.pageSize)); },
    sortedCampaigns() {
      if (!this.sortBy) return this.filteredCampaigns;
      const [field, direction] = this.sortBy.split("-");
      const factor = direction === "desc" ? -1 : 1;
      return this.filteredCampaigns.slice().sort((a, b) => {
        if (field === "name") {
          return factor * (a.name || "").localeCompare(b.name || "", "es");
        }
        const left = a.startAt || "";
        const right = b.startAt || "";
        if (left < right) return -1 * factor;
        if (left > right) return 1 * factor;
        return 0;
      });
    },
    pagedCampaigns() {
      const start = (this.page - 1) * this.pageSize;
      return this.sortedCampaigns.slice(start, start + this.pageSize);
    },
  },
  watch: {
    search() { this.page = 1; },
    statusFilter() { this.page = 1; },
    dateFrom() { this.page = 1; },
    dateTo() { this.page = 1; },
    sortBy() { this.page = 1; },
    pagedCampaigns() { this.loadResponseCounts(); },
  },
  created() { this.loadCampaigns(); },
  methods: {
    async loadCampaigns() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.terrainApi.listCampaigns({ page: 1, size: 200 });
        this.allCampaigns = response.data.items || [];
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.errorMessage = "No fue posible cargar las campañas. Intente nuevamente.";
      } finally {
        this.loading = false;
      }
    },
    onPageChanged(page) { this.page = page; },
    clearFilters() {
      this.search = "";
      this.statusFilter = "";
      this.dateFrom = "";
      this.dateTo = "";
      this.sortBy = "";
    },
    statusLabel(status) {
      return ({ draft: "Borrador", published: "Publicada", closed: "Cerrada" })[status] || "No disponible";
    },
    // Una campaña eliminada (soft delete) ya no acepta envíos ni resuelve su
    // Capa_Terreno en ningún mapa (services/terrain/campaign_lifecycle.go),
    // así que enlace/compartir/copiar deben quedar deshabilitados igual que
    // para una no publicada, aunque su status siga en "published".
    isActive(campaign) { return campaign.status === "published" && !campaign.deletedAt && !!campaign.publicAccessId; },
    campaignLinkUrl(campaign) {
      // El enlace compartido siempre pasa por el control de acceso público:
      // solicita el correo para identificar al respondente, sin iniciar una
      // sesión de usuario de la plataforma. La ruta del formulario recibe
      // solamente la sesión externa, de alcance Terrain, creada después.
      const resolved = this.$router.resolve({ name: "terrain-public-access", params: { publicAccessId: campaign.publicAccessId } });
      return `${window.location.origin}${resolved.href}`;
    },
    openCampaignLink(campaign) {
      if (!this.isActive(campaign)) return;
      window.open(this.campaignLinkUrl(campaign), "_blank", "noopener");
    },
    async shareCampaignLink(campaign) {
      if (!this.isActive(campaign) || !this.hasShareApi) return;
      try {
        await navigator.share({
          title: campaign.name,
          text: `Responde la campaña de terreno: ${campaign.name}`,
          url: this.campaignLinkUrl(campaign),
        });
      } catch (error) {
        // El usuario canceló el cuadro de compartir nativo: no es un error.
      }
    },
    async copyCampaignLink(campaign) {
      if (!this.isActive(campaign)) return;
      try {
        await navigator.clipboard.writeText(this.campaignLinkUrl(campaign));
        this.$store.dispatch("toast/show", { message: "Enlace copiado al portapapeles.", type: "success" });
      } catch (error) {
        this.$store.dispatch("toast/show", { message: "No se pudo copiar el enlace.", type: "error" });
      }
    },
    // Siempre refresca el conteo de la página visible (no solo la primera
    // vez): se usa para gatear el botón "Eliminar para siempre" de la
    // papelera, así que un conteo cacheado y desactualizado no debe dejarlo
    // visible en una campaña que en realidad ya tiene respuestas.
    async loadResponseCounts() {
      await Promise.all(
        this.pagedCampaigns.map(async (campaign) => {
          try {
            const response = await this.terrainApi.listResponses({ campaignId: campaign.id, page: 1, size: 1 });
            this.$set(this.responseCounts, campaign.id, response.data.total);
          } catch (error) {
            this.$set(this.responseCounts, campaign.id, null);
          }
        })
      );
    },
    // dateLabel: la campaña ahora es por día completo en la zona fija
    // America/Santiago (Big Boss) — muestra solo el día, sin hora (que
    // siempre es 00:00 y no aporta nada). Intl con timeZone resuelve el día
    // calendario correcto sin matemática de offset/DST manual.
    dateLabel(value) {
      if (!value) return "Sin fecha";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Sin fecha" : new Intl.DateTimeFormat("es-CL", { timeZone: "America/Santiago" }).format(date);
    },
    // closeDisplayInstant: closeAt persistido es 00:00 del día SIGUIENTE al
    // día de cierre elegido (cierre exclusivo, ver
    // campaign_service.go::parseCampaignDay) — restar 1s antes de formatear
    // muestra el día de cierre real en vez del día siguiente.
    closeDisplayInstant(value) {
      if (!value) return value;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? value : new Date(date.getTime() - 1000).toISOString();
    },
    async openMapModal(campaign) {
      this.mapModalCampaign = campaign;
      this.mapModalOptions = [];
      this.mapModalLoading = true;
      this.$modal.show("terrain-campaign-map-picker");
      try {
        const destinationIds = new Set(campaign.destinationMapIds || []);
        if (!destinationIds.size) return;
        const backendurl = this.$store.getters.backendurl;
        const response = await this.$http.get(`${backendurl}api/v1/maps/`);
        this.mapModalOptions = (response.data || [])
          .filter((map) => destinationIds.has(map.id))
          .map((map) => ({ id: map.id, name: map.name }));
      } catch (error) {
        this.mapModalOptions = [];
      } finally {
        this.mapModalLoading = false;
      }
    },
    closeMapModal() {
      this.$modal.hide("terrain-campaign-map-picker");
    },
    goToMap(mapOption) {
      this.$router.push({
        name: "visor de este mapa",
        params: { idMap: mapOption.id },
        query: { terrainCampaignId: this.mapModalCampaign.id },
      });
      this.closeMapModal();
    },
    // Una campaña ya soft-deleted no tiene sentido "eliminar" de nuevo -
    // el único modo que aplica es hard delete ("eliminar para siempre"),
    // como botón directo. Para una campaña viva, el modo por defecto es
    // soft delete; el modal ofrece hard delete como opción secundaria,
    // solo si un conteo fresco (no el tag cacheado de la tarjeta) confirma
    // cero respuestas.
    openDeleteModal(campaign) {
      this.deleteModalCampaign = campaign;
      this.deleteModalMode = campaign.deletedAt ? "hard" : "soft";
      this.deleteModalResponseCount = null;
      this.$modal.show("terrain-campaign-delete");
      this.terrainApi.listResponses({ campaignId: campaign.id, page: 1, size: 1 }).then((response) => {
        if (this.deleteModalCampaign === campaign) this.deleteModalResponseCount = response.data.total;
      }).catch(() => {
        // Sin conteo confiable, no ofrecemos la opción de eliminar en
        // definitiva (se queda oculta: deleteModalResponseCount !== 0).
      });
    },
    closeDeleteModal() {
      this.$modal.hide("terrain-campaign-delete");
      this.deleteModalCampaign = null;
      this.deleteModalMode = null;
      this.deleteModalResponseCount = null;
    },
    async confirmDelete() {
      const campaign = this.deleteModalCampaign;
      const mode = this.deleteModalMode;
      if (!campaign) return;
      this.deleteModalSubmitting = true;
      try {
        if (mode === "hard") {
          await this.terrainApi.hardDeleteCampaign(campaign.id, campaign.resourceVersion);
          this.allCampaigns = this.allCampaigns.filter((c) => c.id !== campaign.id);
        } else {
          // Soft delete: actualiza el campaign en el listado local (queda
          // con deletedAt/resourceVersion nuevos) en vez de quitarlo, para
          // que pase a verse de inmediato bajo el filtro "papelera" sin
          // recargar la página.
          const response = await this.terrainApi.deleteCampaign(campaign.id, campaign.resourceVersion);
          const index = this.allCampaigns.findIndex((c) => c.id === campaign.id);
          if (index !== -1) this.$set(this.allCampaigns, index, response.data);
        }
        this.$store.dispatch("toast/show", {
          message: mode === "hard" ? "Campaña eliminada en definitiva." : "Campaña eliminada.",
          type: "success",
        });
        this.closeDeleteModal();
      } catch (error) {
        const code = error.response && error.response.data && error.response.data.code;
        const messages = {
          TERRAIN_CAMPAIGN_HAS_RESPONSES: "No se puede eliminar en definitiva: la campaña ya tiene respuestas enviadas.",
          TERRAIN_STALE_RESOURCE: "La campaña fue modificada por otra persona; recargue e intente de nuevo.",
          TERRAIN_CAMPAIGN_ALREADY_DELETED: "La campaña ya había sido eliminada.",
          TERRAIN_RESOURCE_NOT_AVAILABLE: "La campaña ya no está disponible.",
        };
        this.$store.dispatch("toast/show", { message: messages[code] || "No se pudo eliminar la campaña.", type: "error" });
      } finally {
        this.deleteModalSubmitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-campaigns__body {
  display: grid;
  gap: var(--terrain-space-lg);
  margin: 0 auto;
  max-width: 960px;
  width: 95%;
}

.terrain-campaigns__toolbar {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-md);
  justify-content: space-between;
}

.terrain-campaigns__new {
  background: var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  font-weight: 600;
  text-decoration: none;
  transition: background-color var(--terrain-transition);
  white-space: nowrap;

  &:hover { background: var(--color-steel); text-decoration: none; }
}

.terrain-campaigns__filters {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-md);
}

.terrain-campaigns__search,
.terrain-campaigns__filter-field {
  display: grid;
  gap: var(--terrain-space-xs);
  color: var(--color-navy);
  font-weight: 600;
  font-size: var(--terrain-font-caption);

  input {
    border: 1px solid var(--color-line);
    border-radius: var(--terrain-radius-md);
    font: inherit;
    padding: var(--terrain-space-sm) var(--terrain-space-md);
    transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

    &:hover { border-color: var(--color-steel); }
    &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
  }
}

.terrain-campaigns__search { max-width: 320px; }
.terrain-campaigns__filter-field { min-width: 160px; }

.terrain-campaigns__tags {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-sm);
  margin-top: var(--terrain-space-sm);
}

.terrain-campaigns__tag {
  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-graphite);
  display: inline-block;
  font-size: var(--terrain-font-caption);
  font-weight: 700;
  line-height: 1.4;
  padding: 2px var(--terrain-space-sm);
  text-decoration: none;

  &--responses {
    background: var(--color-white);
    border-color: var(--color-navy);
    color: var(--color-navy);
    cursor: pointer;
    transition: background-color var(--terrain-transition), color var(--terrain-transition);

    &:hover { background: var(--color-navy); color: var(--color-white); text-decoration: none; }
  }

  &--draft { background: var(--color-mist); border-color: var(--color-line); color: var(--color-ghost); }
  &--published { background: var(--terrain-color-success-light); border-color: var(--color-success); color: var(--color-success); }
  &--closed { background: var(--terrain-color-warning-light); border-color: var(--color-warning); color: var(--color-warning); }
  &--deleted { background: var(--terrain-color-danger-light); border-color: var(--color-danger); color: var(--color-danger); }
}

.terrain-campaigns__card-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-sm);
}

.terrain-campaigns__icon-btn {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: 50%;
  color: var(--color-navy);
  cursor: pointer;
  display: flex;
  height: 2.25rem;
  justify-content: center;
  width: 2.25rem;
  transition: background-color var(--terrain-transition), color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-navy); color: var(--color-white); }
  &:disabled { background: var(--color-white); border-color: var(--color-line); color: var(--color-ghost); cursor: not-allowed; }

  &--danger {
    border-color: var(--color-danger);
    color: var(--color-danger);

    &:hover:not(:disabled) { background: var(--color-danger); color: var(--color-white); }
  }
}

.terrain-campaigns__map-modal {
  display: grid;
  gap: var(--terrain-space-md);
  padding: var(--terrain-space-lg);
}

.terrain-campaigns__map-modal h2 { margin: 0; color: var(--color-navy); }
.terrain-campaigns__map-modal-status { margin: 0; color: var(--color-ghost); }

.terrain-campaigns__map-modal-list {
  display: grid;
  gap: var(--terrain-space-sm);
  list-style: none;
  margin: 0;
  padding: 0;
}

.terrain-campaigns__map-modal-option {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  color: var(--color-navy);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  text-align: left;
  width: 100%;
  transition: border-color var(--terrain-transition), background-color var(--terrain-transition);

  &:hover { background: var(--color-mist); border-color: var(--color-steel); }
}

.terrain-campaigns__map-modal-option-link {
  background: none;
  border: 0;
  color: var(--color-danger);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 0;
  text-align: left;
  text-decoration: underline;
  width: fit-content;

  &:disabled { color: var(--color-ghost); cursor: not-allowed; }
}

.terrain-campaigns__map-modal-actions { display: flex; justify-content: flex-end; gap: var(--terrain-space-sm); }

.terrain-campaigns__map-modal-cancel {
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-navy);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover { background: var(--color-mist); }
}

.terrain-campaigns__list {
  display: grid;
  gap: var(--terrain-space-md);
  list-style: none;
  margin: 0;
  padding: 0;
}

.terrain-campaigns__card {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  display: flex;
  gap: var(--terrain-space-lg);
  justify-content: space-between;
  padding: var(--terrain-space-lg);
  transition: box-shadow var(--terrain-transition), border-color var(--terrain-transition);

  &:hover { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }

  h3, p { margin: 0; }
  p { color: var(--color-ghost); margin-top: var(--terrain-space-xs); }
}

.terrain-campaigns__link {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-md);
  color: var(--color-white);
  cursor: pointer;
  font: inherit;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-steel); text-decoration: none; }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }

  &--secondary {
    background: var(--color-white);
    border: 1px solid var(--color-navy);
    color: var(--color-navy);

    &:hover:not(:disabled) { background: var(--color-mist); color: var(--color-navy); }
    &:disabled { background: var(--color-white); border-color: var(--color-line); color: var(--color-ghost); }
  }

  &--danger {
    background: var(--color-danger);

    &:hover:not(:disabled) { background: var(--color-danger-dark, var(--color-danger)); }
  }
}

.terrain-campaigns__error {
  background: var(--terrain-color-danger-light);
  border-left: 4px solid var(--color-danger);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-danger);
  margin: 0;
  padding: var(--terrain-space-md);
}

.terrain-campaigns__status,
.terrain-campaigns__empty {
  color: var(--color-ghost);
  margin: 0;
}

@media (max-width: 640px) {
  .terrain-campaigns__card { align-items: stretch; flex-direction: column; }
  .terrain-campaigns__link { width: 100%; }
}
</style>
